import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthPageLayout, AuthForm } from '../components/layout';
import { pageStyles } from '../config/theme';
import LoadingPage from '../components/ui/LoadingPage';
import useAuthStore from '../stores/authStore';
import useGameStore from '../stores/gameStore';
import type { ApiError } from '../types';

type RedeemErrorKind = 'invalid' | 'expired' | 'network' | 'unknown';

function classifyRedeemError(error: unknown): { kind: RedeemErrorKind; message: string } {
  const apiError = error as ApiError;
  const code = apiError?.code ?? '';
  const message = apiError?.message ?? '';

  if (code === 'GUEST_ADVENTURE_INVITE_EXPIRED') {
    return {
      kind: 'expired',
      message: 'This invite link has expired. Ask the host for a new link.',
    };
  }
  if (code === 'GUEST_ADVENTURE_INVITE_NOT_FOUND') {
    return {
      kind: 'invalid',
      message: 'This invite link is not valid. Check the URL or ask the host for a new link.',
    };
  }
  if (code === 'NETWORK_ERROR' || code === 'TIMEOUT') {
    return {
      kind: 'network',
      message: 'Could not reach the server. Check your connection and try again.',
    };
  }
  if (message) {
    return { kind: 'unknown', message };
  }
  return {
    kind: 'unknown',
    message: 'Something went wrong opening this invite. Please try again.',
  };
}

const InviteRedeemPage: React.FC = () => {
  const { token: routeToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const redeemGuestAdventureInvite = useGameStore((state) => state.redeemGuestAdventureInvite);
  const [error, setError] = useState<{ kind: RedeemErrorKind; message: string } | null>(null);
  const [isRedeeming, setIsRedeeming] = useState(true);
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    const token = (routeToken ?? '').trim();
    if (!token) {
      setError({
        kind: 'invalid',
        message: 'This invite link is missing a token. Check the URL or ask the host for a new link.',
      });
      setIsRedeeming(false);
      return;
    }

    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;

    const redeem = async () => {
      setIsRedeeming(true);
      setError(null);

      try {
        const authState = useAuthStore.getState();
        if (authState.isAuthenticated && !authState.isGuest) {
          await useAuthStore.getState().logout();
        }

        const { sessionId, playerId } = await redeemGuestAdventureInvite(token);

        if (!sessionId || !playerId) {
          throw new Error('Invite redeemed but session could not be loaded');
        }

        navigate(`/scene/${sessionId}/${playerId}`, { replace: true });
      } catch (redeemError) {
        console.error('[InviteRedeemPage] Redeem failed:', redeemError);
        setError(classifyRedeemError(redeemError));
        setIsRedeeming(false);
        hasAttemptedRef.current = false;
      }
    };

    void redeem();
  }, [routeToken, redeemGuestAdventureInvite, navigate]);

  if (isRedeeming) {
    return (
      <LoadingPage
        title="Opening your adventure..."
        subtitle="Preparing your guest session"
      />
    );
  }

  if (error) {
    return (
      <AuthPageLayout title="Invite link problem" subtitle="">
        <AuthForm onSubmit={(e) => e.preventDefault()}>
          <div className="px-6 py-4">
            <p style={pageStyles.subtitle}>{error.message}</p>
            {error.kind === 'network' && (
              <p className="mt-4" style={pageStyles.subtitle}>
                You can refresh this page to try again.
              </p>
            )}
          </div>
        </AuthForm>
      </AuthPageLayout>
    );
  }

  return null;
};

export default InviteRedeemPage;
